import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  VpnKey as VpnKeyIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';

const ChangePassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [touched, setTouched] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
    setSuccess('');
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const togglePasswordVisibility = (field) => () => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    if (!formData.currentPassword) return 'Current password is required';
    if (!formData.newPassword) return 'New password is required';
    if (formData.newPassword.length < 6) return 'New password must be at least 6 characters';
    if (formData.newPassword === formData.currentPassword) return 'New password must be different from current password';
    if (!formData.confirmPassword) return 'Please confirm your new password';
    if (formData.newPassword !== formData.confirmPassword) return 'New passwords do not match';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await ApiService.post('/api/users/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      setSuccess('Password changed successfully');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTouched({ currentPassword: false, newPassword: false, confirmPassword: false });
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to change password. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    formData.currentPassword &&
    formData.newPassword &&
    formData.confirmPassword &&
    formData.newPassword === formData.confirmPassword &&
    formData.newPassword !== formData.currentPassword &&
    formData.newPassword.length >= 6;

  const fields = [
    { key: 'currentPassword', show: 'current', label: 'Current password' },
    { key: 'newPassword', show: 'new', label: 'New password' },
    { key: 'confirmPassword', show: 'confirm', label: 'Confirm new password' },
  ];
  const mismatch = touched.confirmPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword;

  return (
    <Box sx={{ height: '100%', minWidth: 0 }}>
      <Paper variant="outlined" component="form" onSubmit={handleSubmit}
        sx={{ p: 2, height: '100%', minWidth: 0, '& .MuiInputBase-root, & .MuiInputLabel-root': { fontSize: '0.8125rem' }, '& .MuiFormHelperText-root': { fontSize: '0.6875rem' } }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Password</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 2 }}>Change the password you sign in with.</Typography>

        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {fields.map((f) => (
            <TextField
              key={f.key}
              size="small"
              fullWidth
              label={f.label}
              type={showPasswords[f.show] ? 'text' : 'password'}
              value={formData[f.key]}
              onChange={handleChange(f.key)}
              onBlur={handleBlur(f.key)}
              error={f.key === 'confirmPassword' && !!mismatch}
              helperText={f.key === 'confirmPassword' && mismatch ? 'Does not match the new password' : f.helper}
              autoComplete={f.key === 'currentPassword' ? 'current-password' : 'new-password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" edge="end" onClick={togglePasswordVisibility(f.show)} aria-label={showPasswords[f.show] ? 'Hide password' : 'Show password'}>
                      {showPasswords[f.show] ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ))}
        </Box>

        {/* Rules, ticked as they are met */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.25 }}>
          {[
            ['6+ characters', formData.newPassword.length >= 6],
            ['Different from current', !!formData.newPassword && formData.newPassword !== formData.currentPassword],
            ['Passwords match', !!formData.confirmPassword && formData.newPassword === formData.confirmPassword],
          ].map(([label, ok]) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: ok ? 'success.main' : 'text.disabled' }}>
              <CheckIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: '0.6875rem', color: 'inherit' }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button type="submit" variant="contained" size="small" disabled={!isFormValid || loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <VpnKeyIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
            {loading ? 'Updating…' : 'Update password'}
          </Button>
        </Box>
      </Paper>

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ fontSize: '0.8125rem' }}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ChangePassword;
